import { useEffect, useState, useCallback } from "react";
import { getMetrics, getTeamMetrics, getUserMetrics, getManagerMetrics, getAlerts, getTimeline, getTeams } from "../api";

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmt    = (n) => Number(n || 0).toLocaleString();
const fmtCost = (n) => {
  const v = Number(n || 0);
  if (v === 0) return "$0.00";
  if (v < 0.01) return `$${v.toFixed(6)}`;
  if (v < 1)    return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
};
const fmtTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const fmtDay = (str) => {
  const [, m, d] = str.split("-");
  return `${m}/${d}`;
};
const RISK_COLORS = { low: "#38a169", medium: "#d97706", high: "#e53e3e", critical: "#9b2c2c" };

// ── Filter bar ─────────────────────────────────────────────────────────────────
const DATE_RANGES = [
  { label: "Today",    days: 1    },
  { label: "7 days",  days: 7    },
  { label: "30 days", days: 30   },
  { label: "90 days", days: 90   },
  { label: "All time", days: null },
];
const DOMAINS     = ["general", "medical", "financial", "legal", "hr", "other"];
const RISK_LEVELS = ["low", "medium", "high", "critical"];
const RISK_CHIP_COLORS = {
  low:      { bg: "#f0fff4", color: "#276749", border: "#9ae6b4" },
  medium:   { bg: "#fffbeb", color: "#92400e", border: "#fcd34d" },
  high:     { bg: "#fff5f5", color: "#c53030", border: "#feb2b2" },
  critical: { bg: "#fff5f5", color: "#9b2c2c", border: "#fc8181" },
};

// Format YYYY-MM-DD → "Sep 15"
const fmtShortDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Today's date as YYYY-MM-DD (local time)
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function Chip({ label, icon, onRemove, colorStyle }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px 3px 10px", borderRadius: 9999, fontSize: 12, fontWeight: 600,
      background: colorStyle?.bg || "var(--primary-light)",
      color:      colorStyle?.color || "var(--primary)",
      border: `1px solid ${colorStyle?.border || "var(--primary)"}`,
      whiteSpace: "nowrap",
    }}>
      {icon && <span>{icon}</span>}
      {label}
      <button onClick={onRemove} style={{
        background: "none", border: "none", cursor: "pointer",
        color: "inherit", fontSize: 14, padding: "0 0 0 2px", lineHeight: 1, opacity: 0.7,
      }}>×</button>
    </span>
  );
}

function FilterBar({ filters, teams, onChange, onClearAll }) {
  // Local UI state — only committed to parent on "Apply"
  const [customOpen, setCustomOpen] = useState(false);
  const [customType, setCustomType] = useState("single"); // "single" | "range"
  const [draftFrom,  setDraftFrom]  = useState(todayIso);
  const [draftTo,    setDraftTo]    = useState(todayIso);

  const isCustomActive = Boolean(filters.date_from);
  const teamName = teams.find((t) => String(t.id) === String(filters.team_id))?.name;
  const activeCount = [filters.team_id, filters.domain, filters.risk_level, filters.date_from]
    .filter(Boolean).length;

  const applyCustom = () => {
    if (!draftFrom) return;
    const to = customType === "range" ? (draftTo || draftFrom) : draftFrom;
    onChange({ days: null, date_from: draftFrom, date_to: to });
    setCustomOpen(false);
  };

  const cancelCustom = () => setCustomOpen(false);

  const openCustom = () => {
    // Pre-fill drafts from existing custom filter if present
    if (filters.date_from) {
      setDraftFrom(filters.date_from);
      setDraftTo(filters.date_to || filters.date_from);
      setCustomType(filters.date_from === filters.date_to ? "single" : "range");
    }
    setCustomOpen((v) => !v);
  };

  // Date chip label
  const dateChipLabel = filters.date_from
    ? (filters.date_from === filters.date_to || !filters.date_to)
        ? fmtShortDate(filters.date_from)
        : `${fmtShortDate(filters.date_from)} → ${fmtShortDate(filters.date_to)}`
    : "";

  const pillStyle = (active) => ({
    padding: "5px 13px", borderRadius: 9999, border: "1.5px solid",
    fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer",
    transition: "all 0.15s",
    background:  active ? "var(--primary)"                  : "var(--surface)",
    color:       active ? "#fff"                             : "var(--muted)",
    borderColor: active ? "var(--primary)"                   : "var(--border)",
    boxShadow:   active ? "0 1px 4px rgba(79,70,229,0.25)"  : "none",
  });

  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)",
                  borderRadius: 10, marginBottom: 24, overflow: "hidden" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 16px", borderBottom: "1px solid var(--border)",
                    background: "var(--bg)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Filters</span>
          {activeCount > 0 && (
            <span style={{ background: "var(--primary)", color: "#fff", borderRadius: 9999,
                           fontSize: 11, fontWeight: 700, padding: "1px 7px" }}>
              {activeCount} active
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button onClick={onClearAll} style={{
            background: "none", border: "1px solid var(--border)", borderRadius: 6,
            fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "3px 10px",
            color: "var(--muted)",
          }}>Clear all ×</button>
        )}
      </div>

      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* ── Period row ── */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
                         letterSpacing: "0.04em", marginTop: 7, width: 64, flexShrink: 0 }}>
            Period
          </span>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            {/* Preset pills */}
            {DATE_RANGES.map((r) => {
              const active = !isCustomActive && filters.days === r.days;
              return (
                <button key={r.label}
                  onClick={() => { onChange({ days: r.days, date_from: null, date_to: null }); setCustomOpen(false); }}
                  style={pillStyle(active)}>
                  {r.label}
                </button>
              );
            })}

            {/* Custom pill */}
            <button onClick={openCustom}
              style={{
                ...pillStyle(isCustomActive || customOpen),
                background: isCustomActive ? "#fff7ed" : customOpen ? "#fff7ed" : "var(--surface)",
                borderColor: isCustomActive || customOpen ? "#f97316" : "var(--border)",
                color:       isCustomActive || customOpen ? "#c2410c" : "var(--muted)",
                boxShadow:   isCustomActive || customOpen ? "0 1px 4px rgba(249,115,22,0.25)" : "none",
              }}>
              Custom {customOpen ? "▲" : "▾"}
            </button>
          </div>
        </div>

        {/* ── Custom date picker panel ── */}
        {customOpen && (
          <div style={{
            marginLeft: 74, background: "#fffbf5", border: "1.5px solid #f97316",
            borderRadius: 8, padding: "14px 16px", display: "flex",
            flexDirection: "column", gap: 12,
          }}>
            {/* Single / Range toggle */}
            <div style={{ display: "flex", gap: 6 }}>
              {[["single", "Specific day"], ["range", "Date range"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setCustomType(val)}
                  style={{
                    padding: "4px 14px", borderRadius: 9999, border: "1.5px solid",
                    fontSize: 13, fontWeight: customType === val ? 700 : 500, cursor: "pointer",
                    background:  customType === val ? "#f97316" : "var(--surface)",
                    color:       customType === val ? "#fff"    : "var(--muted)",
                    borderColor: customType === val ? "#f97316" : "var(--border)",
                  }}>{lbl}</button>
              ))}
            </div>

            {/* Date input(s) */}
            {customType === "single" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e", width: 50 }}>Date</span>
                <input type="date" value={draftFrom} max={todayIso()}
                  onChange={(e) => { setDraftFrom(e.target.value); setDraftTo(e.target.value); }}
                  style={{ width: 160, padding: "6px 10px", borderRadius: 6,
                           border: "1px solid #f97316", fontSize: 13 }} />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e", width: 50 }}>From</span>
                <input type="date" value={draftFrom} max={draftTo || todayIso()}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  style={{ width: 160, padding: "6px 10px", borderRadius: 6,
                           border: "1px solid #f97316", fontSize: 13 }} />
                <span style={{ fontSize: 14, color: "#92400e" }}>→</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>To</span>
                <input type="date" value={draftTo} min={draftFrom} max={todayIso()}
                  onChange={(e) => setDraftTo(e.target.value)}
                  style={{ width: 160, padding: "6px 10px", borderRadius: 6,
                           border: "1px solid #f97316", fontSize: 13 }} />
              </div>
            )}

            {/* Apply / Cancel */}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={applyCustom} disabled={!draftFrom}
                style={{
                  padding: "6px 18px", borderRadius: 6, border: "none",
                  background: "#f97316", color: "#fff", fontSize: 13, fontWeight: 700,
                  cursor: draftFrom ? "pointer" : "not-allowed", opacity: draftFrom ? 1 : 0.5,
                }}>Apply</button>
              <button onClick={cancelCustom}
                style={{
                  padding: "6px 14px", borderRadius: 6,
                  border: "1px solid var(--border)", background: "var(--surface)",
                  fontSize: 13, fontWeight: 500, cursor: "pointer", color: "var(--muted)",
                }}>Cancel</button>
            </div>
          </div>
        )}

        {/* ── Dropdowns ── */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)",
                           textTransform: "uppercase", letterSpacing: "0.04em" }}>Team</span>
            <select value={filters.team_id || ""} onChange={(e) => onChange({ team_id: e.target.value || null })}
              style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid var(--border)", fontSize: 13,
                       background: filters.team_id ? "var(--primary-light)" : "var(--surface)",
                       color:      filters.team_id ? "var(--primary)" : "var(--text)",
                       fontWeight: filters.team_id ? 600 : 400,
                       boxShadow:  filters.team_id ? "0 0 0 2px rgba(79,70,229,0.15)" : "none" }}>
              <option value="">All teams</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)",
                           textTransform: "uppercase", letterSpacing: "0.04em" }}>Domain</span>
            <select value={filters.domain || ""} onChange={(e) => onChange({ domain: e.target.value || null })}
              style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid var(--border)", fontSize: 13,
                       background: filters.domain ? "var(--primary-light)" : "var(--surface)",
                       color:      filters.domain ? "var(--primary)" : "var(--text)",
                       fontWeight: filters.domain ? 600 : 400,
                       boxShadow:  filters.domain ? "0 0 0 2px rgba(79,70,229,0.15)" : "none" }}>
              <option value="">All domains</option>
              {DOMAINS.map((d) => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 150 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)",
                           textTransform: "uppercase", letterSpacing: "0.04em" }}>Risk Level</span>
            <select value={filters.risk_level || ""} onChange={(e) => onChange({ risk_level: e.target.value || null })}
              style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid var(--border)", fontSize: 13,
                       background: filters.risk_level ? (RISK_CHIP_COLORS[filters.risk_level]?.bg  || "var(--surface)") : "var(--surface)",
                       color:      filters.risk_level ? (RISK_CHIP_COLORS[filters.risk_level]?.color || "var(--text)")  : "var(--text)",
                       fontWeight: filters.risk_level ? 600 : 400,
                       boxShadow:  filters.risk_level ? `0 0 0 2px ${RISK_CHIP_COLORS[filters.risk_level]?.border || "#ccc"}50` : "none" }}>
              <option value="">All risk levels</option>
              {RISK_LEVELS.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </label>
        </div>

        {/* ── Active chips ── */}
        {activeCount > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 4,
                        borderTop: "1px solid var(--border)", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Active:</span>
            {filters.date_from && (
              <Chip
                label={dateChipLabel}
                onRemove={() => onChange({ date_from: null, date_to: null, days: 30 })}
                colorStyle={{ bg: "#fff7ed", color: "#c2410c", border: "#f97316" }}
              />
            )}
            {filters.team_id && teamName && (
              <Chip label={teamName} onRemove={() => onChange({ team_id: null })} />
            )}
            {filters.domain && (
              <Chip label={filters.domain.charAt(0).toUpperCase() + filters.domain.slice(1)}
                    onRemove={() => onChange({ domain: null })} />
            )}
            {filters.risk_level && (
              <Chip
                label={filters.risk_level.charAt(0).toUpperCase() + filters.risk_level.slice(1)}
                onRemove={() => onChange({ risk_level: null })}
                colorStyle={RISK_CHIP_COLORS[filters.risk_level]}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Alert banner ───────────────────────────────────────────────────────────────
function AlertBanner({ alerts, onDismiss }) {
  if (!alerts) return null;
  const items = [
    ...alerts.high_volume_users.map((u) => ({
      id: `hv-${u.requester_email}-${u.day}`,
      level: "danger",
      text: `${u.requester} submitted ${u.count} requests on ${u.day} — exceeds the 5/day threshold.`,
    })),
    ...alerts.critical_today.map((r) => ({
      id: `crit-${r.id}`,
      level: "warning",
      text: `Critical-risk request today: "${r.title}" by ${r.requester} (score ${r.risk_score}/10) — submitted ${fmtDate(r.submitted_at)}.`,
    })),
    ...alerts.stale_pending.map((r) => ({
      id: `stale-${r.id}`,
      level: "info",
      text: `Request #${r.id} "${r.title}" has been ${r.status} for ${r.hours_waiting}h without a decision.`,
    })),
  ];

  if (items.length === 0) return null;

  const colors = {
    danger:  { bg: "#fff5f5", border: "#fed7d7", text: "#c53030" },
    warning: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
    info:    { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
      {items.map((item) => {
        const c = colors[item.level];
        return (
          <div key={item.id} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                                       background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8,
                                       padding: "10px 14px", fontSize: 13 }}>
            <span style={{ color: c.text }}>{item.text}</span>
            <button onClick={() => onDismiss(item.id)} style={{ background: "none", border: "none",
              cursor: "pointer", color: c.text, fontSize: 16, padding: "0 0 0 12px", lineHeight: 1 }}>×</button>
          </div>
        );
      })}
    </div>
  );
}

// ── Timeline chart (inline SVG) ────────────────────────────────────────────────
function TimelineChart({ data }) {
  if (!data || data.length === 0) return <div className="empty" style={{ padding: 30 }}>No data for this period.</div>;

  const W = 680, H = 160, PAD = { top: 10, right: 10, bottom: 28, left: 32 };
  const maxVal = Math.max(1, ...data.map((d) => d.total));
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const barW   = Math.max(2, (chartW / data.length) - 1);

  // Y gridlines
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((pct) => ({
    y: PAD.top + chartH * (1 - pct),
    label: Math.round(maxVal * pct),
  }));

  // Show every Nth label to avoid crowding
  const step = Math.ceil(data.length / 10);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {/* Grid lines */}
      {gridLines.map((g) => (
        <g key={g.y}>
          <line x1={PAD.left} y1={g.y} x2={W - PAD.right} y2={g.y}
                stroke="var(--border)" strokeDasharray="3 3" />
          <text x={PAD.left - 4} y={g.y + 4} textAnchor="end"
                fontSize="9" fill="var(--muted)">{g.label}</text>
        </g>
      ))}

      {/* Bars */}
      {data.map((d, i) => {
        const x  = PAD.left + i * (chartW / data.length);
        const bH = d.total === 0 ? 0 : Math.max(2, (d.total / maxVal) * chartH);
        const aH = d.total === 0 ? 0 : Math.max(1, (d.approved / maxVal) * chartH);
        const by = PAD.top + chartH - bH;
        const ay = PAD.top + chartH - aH;

        return (
          <g key={d.day}>
            {/* Total bar */}
            <rect x={x} y={by} width={barW} height={bH}
                  fill="var(--primary)" opacity="0.25" rx="1">
              <title>{d.day}: {d.total} total, {d.approved} approved, {d.rejected} rejected</title>
            </rect>
            {/* Approved overlay */}
            {aH > 0 && (
              <rect x={x} y={ay} width={barW} height={aH}
                    fill="var(--success)" opacity="0.8" rx="1">
                <title>{d.day}: {d.approved} approved</title>
              </rect>
            )}
            {/* X label */}
            {i % step === 0 && (
              <text x={x + barW / 2} y={H - 4} textAnchor="middle"
                    fontSize="9" fill="var(--muted)">{fmtDay(d.day)}</text>
            )}
          </g>
        );
      })}

      {/* Legend */}
      <rect x={W - 110} y={PAD.top} width={10} height={10} fill="var(--primary)" opacity="0.25" />
      <text x={W - 96} y={PAD.top + 9} fontSize="9" fill="var(--muted)">Total</text>
      <rect x={W - 60} y={PAD.top} width={10} height={10} fill="var(--success)" opacity="0.8" />
      <text x={W - 46} y={PAD.top + 9} fontSize="9" fill="var(--muted)">Approved</text>
    </svg>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────────
function StatTile({ label, value, sub, color, onClick }) {
  const clickable = Boolean(onClick);
  return (
    <div
      className="stat-tile"
      onClick={onClick}
      style={{
        cursor: clickable ? "pointer" : "default",
        transition: "box-shadow 0.15s, transform 0.1s",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        if (!clickable) return;
        e.currentTarget.style.boxShadow = "0 4px 14px rgba(79,70,229,0.18)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        if (!clickable) return;
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.transform = "";
      }}
    >
      <div className="label" style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {label}
        {clickable && (
          <span style={{ fontSize: 10, color: "var(--primary)", opacity: 0.7, marginLeft: 2 }}>↗</span>
        )}
      </div>
      <div className="value" style={color ? { color } : {}}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

function BarChart({ data, colorFn }) {
  const max = Math.max(1, ...Object.values(data));
  return (
    <div className="bar-chart">
      {Object.entries(data).map(([key, val]) => (
        <div className="bar-row" key={key}>
          <span className="bar-label">{key}</span>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(val / max) * 100}%`,
              background: colorFn?.(key) || "var(--primary)" }} />
          </div>
          <span className="bar-value">{val}</span>
        </div>
      ))}
    </div>
  );
}

// ── Tab: Overview ──────────────────────────────────────────────────────────────
function Overview({ metrics: m, timeline, onNavigate }) {
  if (!m) return <div className="empty">Loading…</div>;
  const go = (filters = {}) => onNavigate?.("requests", filters);
  return (
    <>
      <div className="stat-grid">
        <StatTile label="Total Requests"  value={fmt(m.total_requests)}
          onClick={() => go()} />
        <StatTile label="Pending Review"  value={fmt(m.verified + m.pending)} color="#2563eb"
          onClick={() => go({ status: "verified" })} />
        <StatTile label="Approved"        value={fmt(m.approved)} color="var(--success)"
          onClick={() => go({ status: "approved" })} />
        <StatTile label="Rejected"        value={fmt(m.rejected)} color="var(--danger)"
          onClick={() => go({ status: "rejected" })} />
        <StatTile label="Approval Rate"   value={`${m.approval_rate}%`}
          onClick={() => go({ status: "approved" })} />
        <StatTile label="Active API Keys" value={fmt(m.active_keys)} color="var(--primary)"
          onClick={() => onNavigate?.("apikeys")} />
        <StatTile label="Total API Calls" value={fmt(m.total_calls)}
          onClick={() => onNavigate?.("apikeys")} />
        <StatTile label="Tokens Used"     value={fmtTokens(m.total_tokens)}
          onClick={() => onNavigate?.("apikeys")} />
        <StatTile label="Estimated Cost"  value={fmtCost(m.total_cost_usd)} color="var(--warning)" sub="across filtered scope"
          onClick={() => onNavigate?.("apikeys")} />
      </div>

      {/* Timeline chart */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Requests Over Time</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            <span style={{ color: "var(--primary)", marginRight: 8 }}>■ Total</span>
            <span style={{ color: "var(--success)" }}>■ Approved</span>
          </span>
        </div>
        <div className="card-body" style={{ padding: "16px 20px 8px" }}>
          <TimelineChart data={timeline} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <div className="card">
          <div className="card-header">By Domain</div>
          <div className="card-body">
            {Object.keys(m.by_domain).length
              ? <BarChart data={m.by_domain} />
              : <div className="empty" style={{ padding: 20 }}>No data.</div>}
          </div>
        </div>
        <div className="card">
          <div className="card-header">By Risk Level</div>
          <div className="card-body">
            {Object.keys(m.by_risk_level).length
              ? <BarChart data={m.by_risk_level} colorFn={(k) => RISK_COLORS[k]} />
              : <div className="empty" style={{ padding: 20 }}>No data.</div>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Model Usage</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Model</th><th>Provider</th><th>Assignments</th><th>API Calls</th><th>Tokens</th><th>Est. Cost</th></tr>
            </thead>
            <tbody>
              {m.by_model.filter((r) => r.assignments > 0).map((r) => (
                <tr key={r.name}>
                  <td><strong>{r.name}</strong></td>
                  <td className="text-muted">{r.provider}</td>
                  <td>{r.assignments}</td>
                  <td>{fmt(r.calls)}</td>
                  <td>{fmtTokens(r.tokens)}</td>
                  <td style={{ fontWeight: 600 }}>{fmtCost(r.cost)}</td>
                </tr>
              ))}
              {m.by_model.filter((r) => r.assignments > 0).length === 0 && (
                <tr><td colSpan={6} className="empty">No assignments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── Tab: Teams ─────────────────────────────────────────────────────────────────
function TeamsTab({ teams }) {
  if (!teams) return <div className="empty">Loading…</div>;
  if (teams.length === 0) return <div className="empty">No teams configured. Add teams from the Teams page.</div>;
  const maxCost = Math.max(1, ...teams.map((t) => t.total_cost_usd));

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 14, marginBottom: 24 }}>
        {teams.map((t) => (
          <div key={t.team_id} className="card">
            <div className="card-body" style={{ padding: "14px 16px" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{t.team_name}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>{t.department || "—"} · {t.manager_name}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                {[["REQUESTS", t.total_requests, "var(--text)"],
                  ["KEYS",     t.active_keys,    "var(--primary)"],
                  ["CALLS",    fmt(t.total_calls), "var(--text)"],
                  ["COST",     fmtCost(t.total_cost_usd), "var(--warning)"]].map(([lbl, val, clr]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: "0.05em" }}>{lbl}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: clr }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: "var(--bg)", borderRadius: 3, height: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 3, background: "var(--primary)",
                              width: `${(t.total_cost_usd / maxCost) * 100}%`, transition: "width .5s" }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">Team Breakdown</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Team</th><th>Dept</th><th>Manager</th><th>Requests</th><th>Approved</th><th>Active Keys</th><th>Calls</th><th>Tokens</th><th>Est. Cost</th></tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.team_id}>
                  <td><strong>{t.team_name}</strong></td>
                  <td className="text-muted">{t.department || "—"}</td>
                  <td className="text-muted">{t.manager_name}</td>
                  <td>{t.total_requests}</td>
                  <td>{t.approved_requests}</td>
                  <td style={{ fontWeight: 700, color: "var(--primary)" }}>{t.active_keys}</td>
                  <td>{fmt(t.total_calls)}</td>
                  <td>{fmtTokens(t.total_tokens)}</td>
                  <td style={{ fontWeight: 700, color: t.total_cost_usd > 0 ? "var(--warning)" : "var(--muted)" }}>
                    {fmtCost(t.total_cost_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ── Tab: Users & Manager ───────────────────────────────────────────────────────
function UsersTab({ users }) {
  const [managerEmail, setManagerEmail] = useState("");
  const [managerData, setManagerData]   = useState(null);
  const [loading, setLoading]           = useState(false);
  const [err, setErr]                   = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    if (!managerEmail.trim()) return;
    setLoading(true); setErr(""); setManagerData(null);
    try { setManagerData(await getManagerMetrics(managerEmail.trim())); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const UserTable = ({ rows, colSpan = 9 }) => (
    <div className="table-wrap">
      <table>
        <thead>
          <tr><th>#</th><th>Name</th><th>Email</th><th>Team</th><th>Requests</th><th>Approved</th><th>Keys</th><th>Calls</th><th>Tokens</th><th>Cost</th></tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={10} className="empty">No data.</td></tr>
            : rows.map((u, i) => (
            <tr key={u.requester_email || u.email}>
              <td className="text-muted">{i + 1}</td>
              <td><strong>{u.requester}</strong></td>
              <td className="text-muted">{u.requester_email}</td>
              <td className="text-muted">{u.team_name || "—"}</td>
              <td>{u.total_requests}</td>
              <td>{u.approved_requests ?? u.approved}</td>
              <td style={{ fontWeight: 700, color: "var(--primary)" }}>{u.active_keys}</td>
              <td>{fmt(u.total_calls)}</td>
              <td>{fmtTokens(u.total_tokens)}</td>
              <td style={{ fontWeight: 700, color: u.total_cost_usd > 0 ? "var(--warning)" : "var(--muted)" }}>
                {fmtCost(u.total_cost_usd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {/* Manager lookup */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">Manager's Reportees View</div>
        <div className="card-body">
          <form onSubmit={lookup} style={{ display: "flex", gap: 10, marginBottom: managerData ? 20 : 0 }}>
            <input type="email" placeholder="Enter manager's email…" value={managerEmail}
              onChange={(e) => setManagerEmail(e.target.value)} style={{ maxWidth: 320 }} />
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? "Loading…" : "View Reportees"}
            </button>
            {managerData && <button className="btn btn-outline" type="button"
              onClick={() => { setManagerData(null); setManagerEmail(""); }}>Clear</button>}
          </form>
          {err && <div className="error-msg">{err}</div>}

          {managerData && (
            <>
              <div className="stat-grid" style={{ marginBottom: 16 }}>
                {[["Total Requests", fmt(managerData.summary.total_requests), null],
                  ["Approved",       fmt(managerData.summary.approved),       "var(--success)"],
                  ["Active Keys",    fmt(managerData.summary.active_keys),    "var(--primary)"],
                  ["Total Calls",    fmt(managerData.summary.total_calls),    null],
                  ["Tokens",         fmtTokens(managerData.summary.total_tokens), null],
                  ["Cost",           fmtCost(managerData.summary.total_cost_usd), "var(--warning)"],
                ].map(([lbl, val, clr]) => <StatTile key={lbl} label={lbl} value={val} color={clr} />)}
              </div>

              {managerData.teams.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Teams</div>
                  <div className="table-wrap" style={{ marginBottom: 20 }}>
                    <table>
                      <thead><tr><th>Team</th><th>Dept</th><th>Requests</th><th>Approved</th><th>Keys</th><th>Calls</th><th>Tokens</th><th>Cost</th></tr></thead>
                      <tbody>
                        {managerData.teams.map((t) => (
                          <tr key={t.team_id}>
                            <td><strong>{t.team_name}</strong></td>
                            <td className="text-muted">{t.department || "—"}</td>
                            <td>{t.total_requests}</td>
                            <td>{t.approved}</td>
                            <td style={{ fontWeight: 700, color: "var(--primary)" }}>{t.active_keys}</td>
                            <td>{fmt(t.total_calls)}</td>
                            <td>{fmtTokens(t.total_tokens)}</td>
                            <td style={{ fontWeight: 700, color: "var(--warning)" }}>{fmtCost(t.total_cost_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {managerData.users.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Reportees</div>
                  <UserTable rows={managerData.users} />
                </>
              )}

              {managerData.teams.length === 0 && managerData.users.length === 0 && (
                <div className="empty" style={{ padding: "12px 0" }}>No teams or activity found for this manager.</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Global user leaderboard */}
      <div className="card">
        <div className="card-header">All Users — Usage Leaderboard (sorted by cost)</div>
        {!users
          ? <div className="empty">Loading…</div>
          : <UserTable rows={users} />}
      </div>
    </>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "teams",    label: "Teams"    },
  { id: "users",    label: "Users"    },
];

const DEFAULT_FILTERS = { days: 30, date_from: null, date_to: null, team_id: null, domain: null, risk_level: null };

export default function Dashboard({ onNavigate }) {
  const [tab,         setTab]         = useState("overview");
  const [filters,     setFilters]     = useState(DEFAULT_FILTERS);
  const [teams,       setTeams]       = useState([]);
  const [metrics,     setMetrics]     = useState(null);
  const [teamMetrics, setTeamMetrics] = useState(null);
  const [userMetrics, setUserMetrics] = useState(null);
  const [timeline,    setTimeline]    = useState(null);
  const [alerts,      setAlerts]      = useState(null);
  const [dismissed,   setDismissed]   = useState(new Set());

  // Build API params — date_from/date_to take priority over days
  const buildParams = (f) => {
    const p = {};
    if (f.date_from) {
      p.date_from = f.date_from;
      if (f.date_to) p.date_to = f.date_to;
    } else if (f.days != null) {
      p.days = f.days;
    }
    if (f.team_id)    p.team_id    = f.team_id;
    if (f.domain)     p.domain     = f.domain;
    if (f.risk_level) p.risk_level = f.risk_level;
    return p;
  };

  const load = useCallback(() => {
    const p  = buildParams(filters);
    // Timeline params: pass dates if custom, else pass days (default 30)
    const tp = { ...(filters.team_id ? { team_id: filters.team_id } : {}) };
    if (filters.date_from) {
      tp.date_from = filters.date_from;
      if (filters.date_to) tp.date_to = filters.date_to;
    } else {
      tp.days = filters.days ?? 30;
    }

    Promise.all([
      getMetrics(p),
      getTeamMetrics(p),
      getUserMetrics(p),
      getTimeline(tp),
      getAlerts(),
      getTeams(),
    ]).then(([m, t, u, tl, al, tm]) => {
      setMetrics(m);
      setTeamMetrics(t);
      setUserMetrics(u);
      setTimeline(tl);
      setAlerts(al);
      setTeams(tm);
    }).catch(console.error);
  }, [JSON.stringify(filters)]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Accept either setFilter("key", val) or setFilter({ key: val, ... })
  const setFilter = (keyOrObj, val) => {
    if (typeof keyOrObj === "object") {
      setFilters((f) => ({ ...f, ...keyOrObj }));
    } else {
      setFilters((f) => ({ ...f, [keyOrObj]: val }));
    }
  };

  const visibleAlerts = alerts
    ? {
        ...alerts,
        high_volume_users: alerts.high_volume_users.filter((u) => !dismissed.has(`hv-${u.requester_email}-${u.day}`)),
        critical_today:    alerts.critical_today.filter((r) => !dismissed.has(`crit-${r.id}`)),
        stale_pending:     alerts.stale_pending.filter((r) => !dismissed.has(`stale-${r.id}`)),
      }
    : null;

  const totalAlerts = visibleAlerts
    ? visibleAlerts.high_volume_users.length + visibleAlerts.critical_today.length + visibleAlerts.stale_pending.length
    : 0;

  return (
    <>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1>Dashboard</h1>
          {totalAlerts > 0 && (
            <span style={{ background: "var(--danger)", color: "#fff", borderRadius: 9999,
                           fontSize: 12, fontWeight: 700, padding: "2px 8px" }}>
              {totalAlerts} alert{totalAlerts > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <p>AI request governance — team spend, user activity, model usage.</p>
      </div>

      <div className="page-body">
        {/* Alerts */}
        <AlertBanner
          alerts={visibleAlerts}
          onDismiss={(id) => setDismissed((s) => new Set([...s, id]))}
        />

        {/* Filters */}
        <FilterBar
          filters={filters}
          teams={teams}
          onChange={setFilter}
          onClearAll={() => setFilters(DEFAULT_FILTERS)}
        />

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: "2px solid var(--border)", marginBottom: 24 }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "10px 20px", border: "none", background: "transparent",
              cursor: "pointer", fontWeight: 600, fontSize: 14,
              color: tab === t.id ? "var(--primary)" : "var(--muted)",
              borderBottom: tab === t.id ? "2px solid var(--primary)" : "2px solid transparent",
              marginBottom: -2,
            }}>{t.label}</button>
          ))}
        </div>

        {tab === "overview" && <Overview metrics={metrics} timeline={timeline} onNavigate={onNavigate} />}
        {tab === "teams"    && <TeamsTab teams={teamMetrics} />}
        {tab === "users"    && <UsersTab users={userMetrics} />}
      </div>
    </>
  );
}
